import { FunctionComponent } from "react";
import { Button, Card, Col, Empty, Form, Row, Tag, Typography } from "antd";
import {
    BookOutlined,
    CalendarOutlined,
    DeleteOutlined,
    EditOutlined,
    LeftOutlined,
    PlusOutlined,
    RightOutlined,
    SettingOutlined,
    TeamOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { ClasseViewProps } from "./types";
import { ClasseDtoF, JourActiviteEnum } from "../../../services/classe";
import { NiveauInterne } from "../../../services/inscription";
import dayjs from "dayjs";

const { Text } = Typography;

const JOUR_LABELS: Record<JourActiviteEnum, string> = {
    [JourActiviteEnum.SAMEDI_MATIN]: 'Sam. matin',
    [JourActiviteEnum.DIMANCHE_MATIN]: 'Dim. matin',
    [JourActiviteEnum.DIMANCHE_APRES_MIDI]: 'Dim. ap.-midi',
    [JourActiviteEnum.MERCREDI_APRES_MIDI]: 'Mer. ap.-midi',
};

const NIVEAU_COLOR: Partial<Record<NiveauInterne, string>> = {
    [NiveauInterne.P1]: 'purple',
    [NiveauInterne.P2]: 'purple',
    [NiveauInterne.N1_1]: 'blue',
    [NiveauInterne.N1_2]: 'blue',
    [NiveauInterne.N2_1]: 'cyan',
    [NiveauInterne.N2_2]: 'cyan',
    [NiveauInterne.N3_1]: 'geekblue',
    [NiveauInterne.N3_2]: 'geekblue',
    [NiveauInterne.N4_1]: 'volcano',
    [NiveauInterne.N4_2]: 'volcano',
    [NiveauInterne.DEBUTANT]: 'green',
    [NiveauInterne.INTERMEDIAIRE]: 'orange',
    [NiveauInterne.AVANCE]: 'red',
};

export const ClasseMobileView: FunctionComponent<ClasseViewProps> = ({
    form,
    classes,
    debutAnneeScolaire,
    onCreateClasse,
    onModifierClasse,
    onDeleteClasse,
    doSearchClasses,
    handleAnneeScolaireChanged,
}) => {
    const currentAnnee: number = Form.useWatch('anneeDebut', form) ?? debutAnneeScolaire;

    const navigateYear = (delta: number) => {
        const newAnnee = currentAnnee + delta;
        form.setFieldValue('anneeDebut', newAnnee);
        handleAnneeScolaireChanged(newAnnee);
        doSearchClasses({ anneeDebut: newAnnee, anneeFin: newAnnee + 1 });
    };

    const getEnseignantsDisplay = (classe: ClasseDtoF): string => {
        if (!classe.enseignants || classe.enseignants.length === 0) return 'Non renseigné';
        return classe.enseignants.map(e => e.nomPrenom).join(', ');
    };

    const getClasseCard = (classe: ClasseDtoF) => {
        const nbEleves = classe.liensClasseEleves?.length ?? 0;
        const niveauColor = classe.niveau ? (NIVEAU_COLOR[classe.niveau] ?? 'default') : 'default';

        return (
            <Col xs={24} key={classe.id}>
                <Card
                    className="card-focus-effect mes-classes-card mes-classes-card--mobile"
                    bordered
                    title={
                        <div className="mes-classes-card-title-row">
                            <span style={{ fontWeight: 600, fontSize: 15 }}>{classe.libelle}</span>
                            {classe.niveau && (
                                <Tag color={niveauColor} className="mes-classes-niveau-tag">
                                    {classe.niveau}
                                </Tag>
                            )}
                        </div>
                    }
                >
                    <div className="mes-classes-card-info-row">
                        <UserOutlined className="mes-classes-card-info-icon" />
                        <Text className="mes-classes-card-info-text">{getEnseignantsDisplay(classe)}</Text>
                    </div>
                    <div className="mes-classes-card-info-row">
                        <TeamOutlined className="mes-classes-card-info-icon" />
                        <Text className="mes-classes-card-info-text">
                            <strong>{nbEleves}</strong>&nbsp;{nbEleves <= 1 ? 'élève inscrit' : 'élèves inscrits'}
                        </Text>
                    </div>
                    {classe.activites && classe.activites.length > 0 && (
                        <div className="mes-classes-card-activites">
                            <CalendarOutlined className="mes-classes-card-info-icon" style={{ marginTop: 3 }} />
                            <div className="mes-classes-activites-tags">
                                {classe.activites.map(a => (
                                    <Tag key={a.jour} color="blue">{JOUR_LABELS[a.jour] ?? a.jour}</Tag>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="mes-classes-card-footer admin-classes-card-actions">
                        <Button
                            icon={<EditOutlined />}
                            size="large"
                            onClick={() => onModifierClasse(classe)}
                            style={{ flex: 1 }}
                        >
                            Modifier
                        </Button>
                        <Button
                            type="primary"
                            danger
                            size="large"
                            icon={<DeleteOutlined />}
                            onClick={() => onDeleteClasse(classe)}
                        />
                    </div>
                </Card>
            </Col>
        );
    };

    return (
        <Form form={form} initialValues={{ anneeDebut: dayjs().year() }}>
            <Form.Item name="anneeDebut" hidden>
                <input type="hidden" />
            </Form.Item>

            <div className="mes-classes-page mes-classes-page--mobile">
                {/* Hero mobile */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px', paddingBottom: '12px', borderBottom: '3px solid #fa8c16', marginBottom: '0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '10px', backgroundColor: '#fa8c16', flexShrink: 0 }}>
                        <SettingOutlined style={{ fontSize: '20px', color: '#ffffff' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Typography.Title level={4} style={{ margin: 0, lineHeight: 1.2 }}>
                            Admin classes
                        </Typography.Title>
                    </div>
                    {/* Navigateur d'année compact */}
                    <div className="mes-classes-year-nav mes-classes-year-nav--mobile">
                        <Button
                            type="text"
                            icon={<LeftOutlined />}
                            onClick={() => navigateYear(-1)}
                            className="mes-classes-year-btn mes-classes-year-btn--mobile"
                            style={{ color: '#fa8c16' }}
                            {...({ testid: "annee-precedente" } as any)}
                        />
                        <span className="mes-classes-year-label mes-classes-year-label--mobile" style={{ color: '#92400e' }}>
                            <strong>{currentAnnee}–{currentAnnee + 1}</strong>
                        </span>
                        <Button
                            type="text"
                            icon={<RightOutlined />}
                            onClick={() => navigateYear(1)}
                            className="mes-classes-year-btn mes-classes-year-btn--mobile"
                            style={{ color: '#fa8c16' }}
                            {...({ testid: "annee-suivante" } as any)}
                        />
                    </div>
                </div>

                {/* CTA + stats */}
                <div className="mes-classes-stats-bar mes-classes-stats-bar--mobile" style={{ justifyContent: 'space-between' }}>
                    {classes.length > 0 ? (
                        <>
                            <span className="mes-classes-mobile-summary">
                                <BookOutlined style={{ marginRight: 6 }} />
                                <strong>{classes.length}</strong>&nbsp;{classes.length <= 1 ? 'classe' : 'classes'}
                            </span>
                            <span className="mes-classes-mobile-summary">
                                <TeamOutlined style={{ marginRight: 6 }} />
                                <strong>{classes.reduce((acc, c) => acc + (c.liensClasseEleves?.length ?? 0), 0)}</strong>&nbsp;élèves
                            </span>
                        </>
                    ) : <span />}
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={onCreateClasse}
                        size="small"
                        style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16' }}
                    >
                        Nouvelle classe
                    </Button>
                </div>

                {/* Grille */}
                <div className="mes-classes-grid-container mes-classes-grid-container--mobile">
                    {classes.length === 0 ? (
                        <div className="mes-classes-empty">
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <span>
                                        Aucune classe pour&nbsp;
                                        <strong>{currentAnnee}–{currentAnnee + 1}</strong>
                                    </span>
                                }
                            >
                                <div className="mes-classes-empty-actions">
                                    <Button onClick={() => navigateYear(-1)} icon={<LeftOutlined />} block>Année précédente</Button>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={onCreateClasse}
                                        block
                                        style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16' }}
                                    >
                                        Créer une classe
                                    </Button>
                                    <Button onClick={() => navigateYear(1)} icon={<RightOutlined />} block>Année suivante</Button>
                                </div>
                            </Empty>
                        </div>
                    ) : (
                        <Row gutter={[0, 16]}>
                            {classes.map(classe => getClasseCard(classe))}
                        </Row>
                    )}
                </div>
            </div>
        </Form>
    );
};
